import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrollment, setEnrollment] = useState(null); // {secret, otpauth_uri, qr_png}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/2fa/status");
      setStatus(data);
    } catch { toast.error("Failed to load security status"); }
  };
  useEffect(() => { load(); }, []);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/2fa/setup");
      setEnrollment(data);
      setCode("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Setup failed";
      toast.error(typeof msg === "string" ? msg : "Setup failed");
    } finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/2fa/enable", { code: code.trim() });
      setRecovery(data.recovery_codes || []);
      setEnrollment(null);
      setCode("");
      toast.success("2FA is now enabled");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Enable failed";
      toast.error(typeof msg === "string" ? msg : "Enable failed");
    } finally { setBusy(false); }
  };

  const copyRecovery = async () => {
    if (!recovery) return;
    try {
      await navigator.clipboard.writeText(recovery.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copy failed"); }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/2fa/disable", { password: disablePwd, code: disableCode.trim() });
      toast.success("2FA disabled");
      setDisableOpen(false);
      setDisablePwd(""); setDisableCode("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Disable failed";
      toast.error(typeof msg === "string" ? msg : "Disable failed");
    } finally { setBusy(false); }
  };

  const enabled = !!status?.enabled;

  return (
    <div className="space-y-8" data-testid="admin-security-page">
      <div>
        <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive Content Studio</p>
        <h1 className="mt-2 font-heading text-3xl font-light tracking-tight md:text-4xl">Security.</h1>
        <p className="mt-2 font-body text-slate-500">Two-factor authentication for board-level access control.</p>
      </div>

      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`grid h-11 w-11 place-items-center rounded-full ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-navy">Two-factor authentication</h2>
              <p className="mt-1 font-body text-sm text-slate-500">
                {enabled
                  ? `Enabled. ${status?.recovery_codes_remaining ?? 0} recovery code${(status?.recovery_codes_remaining ?? 0) === 1 ? "" : "s"} remaining.`
                  : "Not enabled. Add a second step to protect the studio."}
              </p>
            </div>
          </div>
          <div>
            {enabled ? (
              <button data-testid="security-disable-open" onClick={() => setDisableOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 font-body text-sm font-semibold text-rose-700 hover:bg-rose-100">
                Disable 2FA
              </button>
            ) : (
              <button data-testid="security-enable-start" disabled={busy} onClick={beginSetup} className="inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy disabled:opacity-60">
                <KeyRound className="h-4 w-4" /> Enable 2FA
              </button>
            )}
          </div>
        </div>

        {enrollment && (
          <form onSubmit={confirmEnable} data-testid="security-enrollment" className="mt-8 grid gap-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 md:grid-cols-[240px_1fr]">
            <div>
              <img src={enrollment.qr_png} alt="Scan with authenticator" className="h-56 w-56 rounded-md border border-slate-200 bg-white p-2" data-testid="security-qr" />
              <p className="mt-3 font-body text-[11px] text-slate-500">Or enter this secret manually:</p>
              <code className="mt-1 block break-all rounded bg-white px-2 py-1 font-mono text-[11px] text-navy">{enrollment.secret}</code>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-navy">Scan the QR</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5 font-body text-sm text-slate-600">
                <li>Open Google Authenticator, 1Password, or Authy.</li>
                <li>Scan the QR code or paste the secret.</li>
                <li>Enter the 6-digit code shown by the app.</li>
              </ol>
              <label className="mt-5 block font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Code from app</label>
              <input
                data-testid="security-enrollment-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                className="mt-1 w-40 rounded-md border border-slate-200 bg-white px-3 py-2.5 font-body text-lg tracking-[0.4em] outline-none focus:border-royal"
              />
              <div className="mt-4 flex gap-2">
                <button data-testid="security-enrollment-confirm" disabled={busy || !code.trim()} className="inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy disabled:opacity-60">
                  {busy ? "Verifying…" : "Confirm and enable"}
                </button>
                <button type="button" onClick={() => { setEnrollment(null); setCode(""); }} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 font-body text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {recovery && (
          <div data-testid="security-recovery" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-amber-900">Save these recovery codes</h3>
                <p className="mt-1 font-body text-sm text-amber-800">Store them somewhere safe. Each code works once if you lose access to your authenticator.</p>
              </div>
              <button onClick={copyRecovery} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 font-body text-xs font-semibold text-amber-900 hover:bg-amber-100">
                {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy all</>}
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
              {recovery.map((c) => (
                <code key={c} className="rounded bg-white px-3 py-2 font-mono text-sm tracking-wider text-navy">{c}</code>
              ))}
            </div>
            <button onClick={() => setRecovery(null)} className="mt-4 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-4 py-1.5 font-body text-xs font-semibold text-amber-900 hover:bg-amber-100">
              I saved them
            </button>
          </div>
        )}
      </div>

      {disableOpen && (
        <div data-testid="security-disable-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4" onClick={() => setDisableOpen(false)}>
          <div className="w-full max-w-md rounded-[1.25rem] border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h3 className="font-heading text-lg font-bold text-navy">Disable two-factor</h3>
              <button onClick={() => setDisableOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submitDisable} className="space-y-4 p-6">
              <div>
                <label className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Current password</label>
                <input data-testid="security-disable-password" required type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2.5 font-body text-sm outline-none focus:border-royal" />
              </div>
              <div>
                <label className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Authenticator or recovery code</label>
                <input data-testid="security-disable-code" required value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2.5 font-body text-lg tracking-[0.3em] outline-none focus:border-royal" placeholder="000000" />
              </div>
              <button data-testid="security-disable-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-600 px-6 py-3 font-body text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
                {busy ? "Disabling…" : "Disable 2FA"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
